import SEOHead from "@/components/SEOHead";
import { Link } from "react-router-dom";
import { CLINIC } from "@/data/clinic";

const PAGES = {
  privacy: {
    slug: "privacy-policy",
    title: "Privacy Policy",
    metaTitle: "Privacy Policy | Dr. Ashutosh Mishra",
    metaDesc: "How Dr. Ashutosh Mishra's website collects, uses and protects your personal information.",
    body: [
      ["Information We Collect", "We collect only the information you voluntarily provide through our appointment and contact forms — your name, phone number, email and the message you write. We do not collect medical reports through the website; please bring those directly to the clinic."],
      ["How We Use Your Information", "The information is used solely to confirm and schedule your appointment, respond to your queries, and provide better continuity of care. We do not sell or share your personal information with third parties for marketing purposes."],
      ["Cookies & Analytics", "We use Google Analytics to understand how visitors use our site. These tools collect anonymised data such as pages visited and approximate location. No personally identifiable information is collected unless you submit it through a form."],
      ["Data Security", "Submitted information is stored securely. Access is restricted to authorised clinic staff."],
      ["Your Rights", "You may at any time request access to, correction of, or deletion of personal data you have submitted by writing to " + CLINIC.email + "."],
      ["Updates", "This Privacy Policy may be updated. The most recent version will always be available on this page."],
    ],
  },
  terms: {
    slug: "terms",
    title: "Terms & Conditions",
    metaTitle: "Terms & Conditions | Dr. Ashutosh Mishra",
    metaDesc: "Terms governing use of Dr. Ashutosh Mishra's website and online appointment system.",
    body: [
      ["Acceptance of Terms", "By using this website you agree to these Terms & Conditions. If you do not agree, please do not use the website."],
      ["Use of Content", "All content on this website is the intellectual property of " + CLINIC.doctorName + " and is provided for informational purposes only. You may not reproduce, distribute or commercially exploit any content without prior written permission."],
      ["Online Appointment", "An appointment request submitted through the website is a request — confirmation will be communicated by phone or WhatsApp during clinic hours. Submission does not guarantee a slot."],
      ["No Doctor-Patient Relationship", "Browsing this website or submitting forms does not create a doctor-patient relationship. A formal relationship begins only after an in-person consultation."],
      ["Limitation of Liability", "Information here is for general education. Always consult a qualified medical professional for diagnosis and treatment decisions. The doctor and the clinic are not liable for any actions taken solely on the basis of website content."],
      ["Governing Law", "These terms are governed by the laws of India. Any disputes are subject to the exclusive jurisdiction of courts in Varanasi, Uttar Pradesh."],
    ],
  },
  disclaimer: {
    slug: "disclaimer",
    title: "Medical Disclaimer",
    metaTitle: "Medical Disclaimer | Dr. Ashutosh Mishra",
    metaDesc: "Medical disclaimer for the content published on Dr. Ashutosh Mishra's website.",
    body: [
      ["Educational Content Only", "All articles, videos and treatment descriptions on this website are for general educational purposes. They are not a substitute for individualised medical advice, diagnosis or treatment."],
      ["Consult a Qualified Doctor", "Please consult " + CLINIC.doctorName + " or another qualified medical professional before starting, stopping or changing any medication or treatment based on information you read here."],
      ["No Emergency Service", "This website is not for medical emergencies. In a medical emergency, call your local emergency number or visit the nearest hospital immediately."],
      ["Third-Party Links", "Some external links may be provided for reference. We do not control or endorse third-party websites and are not responsible for their content."],
      ["Outcomes Vary", "Patient outcomes vary based on age, condition severity, adherence and individual biology. Testimonials reflect personal experiences and should not be considered guarantees of similar results."],
    ],
  },
};

export default function LegalPage({ which }) {
  const p = PAGES[which];
  if (!p) return null;
  return (
    <div data-testid={`legal-${p.slug}`}>
      <SEOHead
        title={p.metaTitle}
        description={p.metaDesc}
        path={`/${p.slug}`}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: p.title, url: `/${p.slug}` },
        ]}
      />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <nav className="text-xs text-slate-500 mb-3">
          <Link to="/" className="hover:text-green-700">Home</Link> <span className="mx-1.5">/</span> <span className="text-slate-700">{p.title}</span>
        </nav>
        <h1 className="font-poppins font-semibold text-4xl sm:text-5xl text-slate-900 leading-tight">{p.title}</h1>
        <div className="mt-3 text-xs text-slate-500">Last updated: {new Date().toLocaleDateString()}</div>
        <div className="mt-10 space-y-8">
          {p.body.map(([h, t], i) => (
            <section key={i}>
              <h2 className="font-poppins font-semibold text-xl text-slate-900">{h}</h2>
              <p className="mt-2 text-slate-600 leading-relaxed">{t}</p>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}
